/**
 * adbi attribution — captures ad click params at module load time (before React
 * Router strips query params from the URL) and manages anonymous identity.
 *
 * MUST be imported before RouterProvider/HydratedRouter mounts so the
 * module-level landingUrl capture runs first.
 */
import async from 'async'
import debug from 'debug'
import Cookies from 'js-cookie'
import {
  ANONYMOUS_ID_KEY,
  CLICK_ID_PARAMS,
  formatClickSentValue,
  parseClickSentStatus,
} from 'src/utils/attribution/helpers'
import { config } from 'src/utils/config'
import { safeUUID } from 'src/utils/genId'
import {
  getLocalStorage,
  getSessionStorage,
  removeSessionStorage,
  setLocalStorage,
  setSessionStorage,
} from 'src/utils/storage'

// Separate key from the original attribution system's CLICK_SENT_AT_KEY so
// adbi's captureAdClick() does not block the existing useAdClickCapture() hook
// from storing the GCLID in userAttributionClickEvent (which drives Google Ads
// postbacks). Both systems must run independently.
const ADBI_CLICK_SENT_AT_KEY = 'adbi:click-sent-at'

const log = debug('pixai:adbi:attribution')

// Module-level capture — runs before HydratedRouter hydrates
const landingUrl =
  typeof window !== 'undefined' ? new URL(window.location.href) : null
if (landingUrl) log('landing URL captured: %s', landingUrl.href)

// Intentionally same key as attribution/helpers.ts ANONYMOUS_ID_KEY so adbi
// and the existing attribution system share the same anonymous ID.
const ANON_ID_KEY = ANONYMOUS_ID_KEY
const ANON_COOKIE = '_adbi_anon' // cross-subdomain cookie on .pixai.art
const ACQUISITION_KEY = 'attribution:acquisition' // UTM + click params for $identify

const ADBI_FETCH_TIMEOUT_MS = 5_000

// In-memory fallback for privacy mode (storage blocked)
let _memoryAnonId: string | null = null

/** Returns ".pixai.art" from "app.pixai.art", or the bare host for localhost. */
function rootDomain(): string {
  if (typeof window === 'undefined') return ''
  const host = window.location.hostname
  const parts = host.split('.')
  if (parts.length <= 2 || /^\d+$/.test(parts[parts.length - 1])) return host
  return '.' + parts.slice(-2).join('.')
}

function getCookie(name: string): string | null {
  return Cookies.get(name) ?? null
}

function setCookie(name: string, value: string, maxAgeDays: number) {
  const domain = rootDomain()
  Cookies.set(name, value, {
    // Omit domain for localhost/IPs — browsers silently reject it.
    // rootDomain() returns '.pixai.art' (dot-prefixed) for real domains,
    // and the bare host for localhost/IPs.
    domain: domain.startsWith('.') ? domain : undefined,
    expires: maxAgeDays,
    path: '/',
    sameSite: 'Lax',
  })
}

export function getOrCreateAnonymousId(): string {
  if (typeof window === 'undefined') return ''

  // 1. cookie (cross-subdomain, most reliable)
  let id = getCookie(ANON_COOKIE)
  if (id) {
    setLocalStorage(ANON_ID_KEY, id)
    return id
  }

  // 2. localStorage (existing attribution system may have already set this)
  id = getLocalStorage(ANON_ID_KEY)
  if (!id) {
    // 3. in-memory fallback (privacy mode)
    id = _memoryAnonId ?? (_memoryAnonId = safeUUID())
  }

  setLocalStorage(ANON_ID_KEY, id)
  setCookie(ANON_COOKIE, id, 400) // 400 days (1st-party cookie max)
  log('anonymous ID created: %s', id)
  return id
}

export function getAnonymousId(): string | null {
  return getCookie(ANON_COOKIE) ?? getLocalStorage(ANON_ID_KEY)
}

/** Returns 'pending' | 'success' | null from sessionStorage. */
export function getClickSentStatus(): 'pending' | 'success' | null {
  return parseClickSentStatus(getSessionStorage(ADBI_CLICK_SENT_AT_KEY))
}

/** Returns stored acquisition properties (UTM + click params), or null. */
export function getAcquisitionData(): Record<string, string> | null {
  const raw = getCookie('_adbi_acq') ?? getLocalStorage(ACQUISITION_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

interface AdClickParams {
  click_id: string
  platform: 'google_ads' | 'meta'
  source: string
  campaign_id?: string
  ad_id?: string
}

function extractAdClickParams(url: URL): AdClickParams | null {
  for (const param of Object.keys(CLICK_ID_PARAMS)) {
    if (param === 'fbc') continue // fbc is a cookie value, not a URL click param
    const click_id = url.searchParams.get(param)
    if (click_id) {
      return {
        click_id,
        source: param,
        platform:
          param === 'fbclid' ? ('meta' as const) : ('google_ads' as const),
        campaign_id: url.searchParams.get('utm_campaign') ?? undefined,
        ad_id: url.searchParams.get('utm_content') ?? undefined,
      }
    }
  }
  return null
}

/** Called once on app load — sends click to adbi if ad click params are found. */
export async function captureAdClick(): Promise<void> {
  if (!config.adbiUrl || !landingUrl) return

  const status = getClickSentStatus()
  if (status === 'pending' || status === 'success') return

  const params = extractAdClickParams(landingUrl)
  if (!params) return

  // Mark pending before request to guard against StrictMode double-fire
  setSessionStorage(ADBI_CLICK_SENT_AT_KEY, formatClickSentValue('pending'))

  const anonymous_id = getOrCreateAnonymousId()
  if (!anonymous_id) return

  // Store acquisition data for later $identify
  const acquisition: Record<string, string> = {
    source: params.source,
    click_id: params.click_id,
  }
  const utmKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ]
  for (const key of utmKeys) {
    const val = landingUrl.searchParams.get(key)
    if (val) acquisition[key] = val
  }
  if (params.campaign_id) acquisition.campaign_id = params.campaign_id

  setLocalStorage(ACQUISITION_KEY, JSON.stringify(acquisition))
  setCookie('_adbi_acq', JSON.stringify(acquisition), 30)

  log(
    'sending ad click: platform=%s click_id=%s',
    params.platform,
    params.click_id,
  )
  const body = JSON.stringify({
    anonymous_id,
    platform: params.platform,
    click_id: params.click_id,
    campaign_id: params.campaign_id,
    ad_id: params.ad_id,
    landing_url: landingUrl.href,
  })
  let succeeded = false
  try {
    await async.retry({ times: 2, interval: 0 }, async () => {
      const response = await fetch(`${config.adbiUrl}/ingest/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: AbortSignal.timeout(ADBI_FETCH_TIMEOUT_MS),
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      succeeded = true
    })
  } catch {
    // network error or server rejection after retries
  }
  if (succeeded) {
    setSessionStorage(ADBI_CLICK_SENT_AT_KEY, formatClickSentValue('success'))
    log('ad click captured: %s', params.click_id)
  } else {
    // Clear pending so the next page load can retry
    removeSessionStorage(ADBI_CLICK_SENT_AT_KEY)
    log('ad click send failed, cleared pending')
  }
}
